import { reactive } from "vue";

import {
  fetchTourProgress,
  hasAutomaticTourOptOut,
  isTerminalTourProgress,
  reconcileTourProgressSnapshot,
} from "./tourProgress.mjs";

/**
 * Shared reactive snapshot of the tour progress.
 *
 * `ProductTourHost` owns the loading and the writes, while page-level help
 * buttons only read it to decide whether a tour still has to be highlighted.
 */
const state = reactive({
  entries: {},
  loaded: false,
  loading: false,
  failed: false,
});

export const tourProgressState = state;

export const buildTourProgressKey = (tourKey, tourVersion) =>
  `${String(tourKey || "").trim()}:${String(tourVersion || "").trim()}`;

export const readTourProgress = (tourKey, tourVersion) =>
  state.entries[buildTourProgressKey(tourKey, tourVersion)] || null;

export const writeTourProgress = (tourKey, tourVersion, progress) => {
  const key = buildTourProgressKey(tourKey, tourVersion);
  state.entries[key] = reconcileTourProgressSnapshot(state.entries[key], progress);
  return state.entries[key];
};

export const setTourProgressEntry = (tourKey, tourVersion, progress) => {
  const key = buildTourProgressKey(tourKey, tourVersion);
  state.entries[key] = progress;
  return state.entries[key];
};

export const listTourProgress = () => Object.values(state.entries);

export const hasTourAutomaticOptOut = () => hasAutomaticTourOptOut(listTourProgress());

/**
 * A tour is "unseen" while the user has neither completed nor dismissed it.
 * Used for the discreet dot on the page help button, which replaced the
 * automatic per-section start.
 */
export const isTourUnseen = (tourKey, tourVersion) =>
  state.loaded && !isTerminalTourProgress(readTourProgress(tourKey, tourVersion));

export const loadTourProgressOnce = async ({ force = false } = {}) => {
  if (state.loading) return false;
  if (state.loaded && !force) return true;
  state.loading = true;
  try {
    const items = await fetchTourProgress();
    const entries = {};
    for (const item of items) {
      entries[buildTourProgressKey(item.tourKey, item.tourVersion)] = item;
    }
    state.entries = entries;
    state.loaded = true;
    state.failed = false;
    return true;
  } catch (error) {
    console.error("[product-tours] progress load failed", error);
    state.failed = true;
    return false;
  } finally {
    state.loading = false;
  }
};

export const resetTourProgressStore = () => {
  state.entries = {};
  state.loaded = false;
  state.loading = false;
  state.failed = false;
};
